import { BigNumber } from "@yjsgames/bignumber";
import {
  GRID,
  MODULE_BRICK_BALANCE,
  worldDifficultyMultiplier,
  worldGoldMultiplier,
} from "../data/balance.js";
import { chapterInWorld, worldForChapter } from "../data/worldData.js";

/** 그리드 한 칸의 배치 정보 (순수 기하 — Phaser 비의존). */
export interface BrickCell {
  readonly col: number;
  readonly row: number;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * 챕터 진행 — 순수 로직 (Phaser 비의존, 단위 테스트 대상).
 * 벽돌 HP/보상 성장 공식 + 그리드 배치 좌표 계산 + 현재/최고 챕터 카운터를 담당한다.
 * 실제 Brick 오브젝트 생성은 GameScene이 layout()을 소비해서 수행한다.
 */
export class ChapterController {
  private _currentChapter: number;
  private _bestChapter: number;

  constructor(currentChapter = 1, bestChapter = 1) {
    this._currentChapter = Math.max(1, Math.floor(currentChapter));
    this._bestChapter = Math.max(this._currentChapter, Math.floor(bestChapter));
  }

  get currentChapter(): number {
    return this._currentChapter;
  }

  get bestChapter(): number {
    return this._bestChapter;
  }

  /** 벽돌 HP = base × growth^(월드 내 챕터-1) × 월드 보정. */
  static computeBrickHp(chapter: number): BigNumber {
    const world = worldForChapter(chapter);
    const localChapter = chapterInWorld(chapter);
    return BigNumber.fromNumber(MODULE_BRICK_BALANCE.brickBaseHp).mul(
      BigNumber.fromNumber(MODULE_BRICK_BALANCE.brickHpGrowth).pow(localChapter - 1),
    ).mul(BigNumber.fromNumber(worldDifficultyMultiplier(world.worldNo, localChapter)));
  }

  /** 벽돌 1개 파괴 보상 골드 = base × growth^(월드 내 챕터-1) × 월드 보정. */
  static computeRewardGold(chapter: number): BigNumber {
    const world = worldForChapter(chapter);
    const localChapter = chapterInWorld(chapter);
    return BigNumber.fromNumber(MODULE_BRICK_BALANCE.brickBaseGold).mul(
      BigNumber.fromNumber(MODULE_BRICK_BALANCE.brickGoldGrowth).pow(localChapter - 1),
    ).mul(BigNumber.fromNumber(worldGoldMultiplier(world.worldNo, localChapter)));
  }

  /** 현재 디자인 너비 기준 그리드 셀 좌표 목록 (중앙 정렬). */
  static layout(designWidth: number): BrickCell[] {
    const { cols, rows, brickWidth, brickHeight, hGap, vGap, fieldTop } = GRID;
    const fieldWidth = cols * brickWidth + (cols - 1) * hGap;
    const startX = (designWidth - fieldWidth) / 2 + brickWidth / 2;
    const colStep = brickWidth + hGap;
    const rowStep = brickHeight + vGap;
    const cells: BrickCell[] = [];
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        cells.push({
          col,
          row,
          x: startX + col * colStep,
          y: fieldTop + brickHeight / 2 + row * rowStep,
          width: brickWidth,
          height: brickHeight,
        });
      }
    }
    return cells;
  }

  /** 다음 챕터로 진행 — currentChapter 증가 + bestChapter 갱신. 반환: 새 currentChapter. */
  advanceChapter(): number {
    this._currentChapter += 1;
    if (this._currentChapter > this._bestChapter) {
      this._bestChapter = this._currentChapter;
    }
    return this._currentChapter;
  }

  /** 환생 등으로 챕터를 1로 초기화 (bestChapter는 유지). */
  resetToChapterOne(): void {
    this._currentChapter = 1;
  }

  /** 패배 등으로 현재 챕터만 지정 챕터로 되돌린다 (bestChapter는 낮추지 않음). */
  resetToChapter(chapter: number): void {
    this._currentChapter = Math.max(1, Math.floor(chapter));
    this._bestChapter = Math.max(this._bestChapter, this._currentChapter);
  }
}
